// JSON-LD builders for the UAE / Malaysia pages.
//
// Separate from utils/local-business-schema.js on purpose. That module's
// LocalBusiness node is the Singapore business (Paya Lebar address, +65 phone,
// areaServed Singapore) and every SG page emits it byte-identical under one
// @id. The expansion markets have no local address yet, so they must NOT emit
// a LocalBusiness: a Service with areaServed = City, provided by the
// organisation, is the honest description of what exists.
//
// The provider below is deliberately not the site-wide ORGANIZATION_ID node:
// that node's name is "StringsSG Singapore", and redefining the same @id with a
// different name on these pages would give Google two conflicting entities.
//
// As everywhere else on the site: no aggregateRating, no review.

import { SITE } from './local-business-schema'
import { buildBreadcrumb, breadcrumbJsonLd } from './breadcrumb'
import { formatMoney } from './format-money'

const LOGO = "https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/logo.png"

export const absoluteUrl = (path) => (path === '/' ? `${SITE}/` : `${SITE}${path}`)

export const marketProvider = Object.freeze({
    "@type": "Organization",
    name: "StringsSG",
    url: `${SITE}/`,
    logo: LOGO,
})

const areaServedFor = (market, city) =>
    city
        ? { "@type": "City", name: city.name, containedInPlace: { "@type": "Country", name: market.countryName } }
        : { "@type": "Country", name: market.countryName }

export const webPageNode = ({ market, path, title, description, inLanguage, dateModified }) => ({
    "@type": "WebPage",
    "@id": `${absoluteUrl(path)}/#webpage`,
    url: absoluteUrl(path),
    name: title,
    description,
    inLanguage: inLanguage || market.locale,
    ...(dateModified ? { dateModified } : {}),
    breadcrumb: { "@id": `${absoluteUrl(path)}/#breadcrumblist` },
})

export const breadcrumbNode = (path, pageTitle) =>
    breadcrumbJsonLd(buildBreadcrumb(path, pageTitle), absoluteUrl(path))

/**
 * offers: [{ name, price }] - numbers in the market's currency, already
 * discounted. Rendered as an OfferCatalog of single-price Offers.
 */
export const serviceNode = ({ market, city, path, name, serviceType, description, offers = [], catalogName }) => {
    const node = {
        "@type": "Service",
        "@id": `${absoluteUrl(path)}/#service`,
        name,
        serviceType: serviceType || name,
        url: absoluteUrl(path),
        description,
        areaServed: areaServedFor(market, city),
        provider: marketProvider,
    }
    if (offers.length) {
        node.hasOfferCatalog = {
            "@type": "OfferCatalog",
            name: catalogName || `${name} prices`,
            itemListElement: offers.map((offer) => ({
                "@type": "Offer",
                name: offer.name,
                price: Number(offer.price).toFixed(2),
                priceCurrency: market.currency,
                availability: "https://schema.org/InStock",
                itemOffered: { "@type": "Service", name: offer.name },
            })),
        }
    }
    return node
}

export const faqNode = (path, faqs) => ({
    "@type": "FAQPage",
    "@id": `${absoluteUrl(path)}/#faq`,
    mainEntity: faqs.slice(0, 10).map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
})

export const articleNode = ({ market, path, headline, description, datePublished, dateModified, inLanguage, image }) => ({
    "@type": "Article",
    "@id": `${absoluteUrl(path)}/#article`,
    headline,
    description,
    inLanguage: inLanguage || market.locale,
    mainEntityOfPage: { "@id": `${absoluteUrl(path)}/#webpage` },
    datePublished,
    dateModified: dateModified || datePublished,
    author: marketProvider,
    publisher: marketProvider,
    ...(image ? { image } : {}),
})

export const graph = (...nodes) =>
    JSON.stringify({ "@context": "https://schema.org", "@graph": nodes.filter(Boolean) })

// "from AED 119.60" style anchors used in titles and meta descriptions.
export const fromPrice = (amount, countryCd, fractionDigits = 2) =>
    `from ${formatMoney(amount, countryCd, fractionDigits)}`
